import React, { useState, useEffect, useCallback } from 'react';
import _ from 'lodash';
import classnames from 'classnames/bind';
import {
  ISearchState as IState,
  ISearchVehicle as IVehicle,
  Replacement,
} from '../../../interface';
import FilterDropdown from '../../FilterDropdown';
import FilterResult from '../../FilterResult';
import style from './index.module.scss';
import instance from '../../../services/httpRequest';
import { pushEvent } from '../../../services/googleAnalytics';
import Loading from '../../Loading';
import localesReplacement from '../../../utils/localesReplacement';
import UsStates from '../../../utils/states';
import AusStates from '../../../utils/ausStates';
import UkCountries from '../../../utils/ukCountries';
import { useDebounce } from 'use-debounce';
import {
  GoogleReCaptchaProvider,
  GoogleReCaptcha,
} from 'react-google-recaptcha-v3';

import {
  ENABLED_LICENSE_PLATE_COUNTRIES,
  LICENSE_PLATE_GEOCODED_STATE,
} from '../../../constants';
import getStateFromGeoCode from '../../../utils/getStateFromGeoCode';

export interface IProps {
  replacements: Replacement;
  onVehicleSelect?: (
    value: string | number,
    model: string,
    vehicleType?: string,
  ) => void;
  selectedWorkshop?: boolean;
  country?: string;
  language: string | undefined;
  captcha: boolean;
  licensePlateRegexes: any;
}

const cx = classnames.bind(style);

function LicensePlateSearch(props: IProps) {
  const {
    onVehicleSelect,
    replacements,
    selectedWorkshop,
    country,
    captcha,
    language,
    licensePlateRegexes,
  } = props;
  const [states, setStates] = useState<IState[]>([]);
  const [state, setState] = useState<string>('');
  const [licensePlate, setLicensePlate] = useState<string>('');
  const [results, setResults] = useState<IVehicle[]>([]);
  const [isMount, setIsMount] = useState<boolean>(false);
  const [finish, setFinish] = useState<boolean>(false);
  const [debounceValue] = useDebounce(licensePlate, 1000);
  const [reCaptchaToken, setRecaptchaToken] = useState<string>('');
  const [refreshReCaptcha, setRefreshReCaptcha] = useState(false);
  const [submitted, setSubmitted] = useState<boolean>(false);
  const [invalidLicensePlate, setInvalidLicensePlate] =
    useState<boolean>(false);
  const [hsnTooltipOpen, sethsnTooltipOpen] = useState<boolean>(false);

  useEffect(() => {
    setLicensePlate(_.toString(debounceValue));
  }, [debounceValue]);

  const onVerify = useCallback((token: any): void => {
    setRecaptchaToken(token);
  }, []);

  const switchStates = (country?: string) => {
    switch (country) {
      case 'UAS':
        setStates(UsStates);
        break;
      case 'AUS':
        setStates(AusStates);
        break;
      case 'GBR':
        setStates(UkCountries);
        break;
      default:
        setStates(UsStates);
        break;
    }
  };

  const setCountryState = (result: any) => {
    let countryState;
    for (const statee of UsStates) {
      if (statee.id === result.isoCode) {
        countryState = statee.name;
      }
    }
    if (countryState) {
      setState(countryState);
    }
  };

  //Get list of US States
  useEffect(() => {
    switchStates(country);
    if (
      navigator.geolocation &&
      LICENSE_PLATE_GEOCODED_STATE.includes(country as string)
    ) {
      navigator.geolocation.getCurrentPosition((position) => {
        const { latitude, longitude } = position.coords;
        getStateFromGeoCode(latitude, longitude).then((result: any) => {
          if (result?.isoCode) {
            setCountryState(result);
          }
        });
      });
    }
  }, [country]);

  const getSearchPlates = () => {
    instance
      .get(
        `/usaLicensePlateSearch?text=${licensePlate}&stateCode=${states.find((x: { name: any }) => x.name === state)?.id || 'STA' // NOTE: Dummy state for other countries which doesn't need a state parameter
        }`,
      )
      .then((d: { status: any; data: any }) => {
        if (_.inRange(d.status, 200, 300)) {
          setResults(d.data);
        } else {
          setResults([]);
        }
        setIsMount(false);
        setFinish(true);
        setRefreshReCaptcha(true);
        setSubmitted(false);
        setInvalidLicensePlate(false);
      });
  };

  const dealWithError = () => {
    let isError = true;

    if (licensePlateRegexes) {
      for (let element = 0; element < licensePlateRegexes.length; element++) {
        if (licensePlate.match(licensePlateRegexes[element])) {
          isError = false;
        }
      }
    }
    return isError;
  };

  const captchaHandler = () => {
    if (captcha) {
      if (!reCaptchaToken) {
        setResults([]);
        return;
      }
      _.assign(instance.defaults.headers, {
        Authorization: `Captcha ${reCaptchaToken}`,
      });
    } else {
      _.unset(instance.defaults.headers, 'Authorization');
    }
  };

  // Search  by license plate
  useEffect(() => {
    if (!submitted) {
      return;
    }

    const isError = dealWithError();

    if (isError) {
      setResults([]);
      setIsMount(false);
      setFinish(true);
      setRefreshReCaptcha(true);
      setSubmitted(false);
      setInvalidLicensePlate(true);
      return;
    }


    // If It's enabled with state
    if (!state && ENABLED_LICENSE_PLATE_COUNTRIES.includes(country || '')) {
      setFinish(false);
      setResults([]);
      setSubmitted(false);
      setInvalidLicensePlate(false);
      return;
    }
    if (_.trim(licensePlate).length >= 3) {
      setInvalidLicensePlate(false);
      setFinish(false);
      setIsMount(true);
      captchaHandler();
      pushEvent(
        'licensePlateSearch',
        'licensePlateSearch',
        'product finder',
        licensePlate,
        selectedWorkshop ? 'workshop owner' : 'vehicle owner',
        country || '',
      );
      getSearchPlates();
    } else {
      setInvalidLicensePlate(false);
      setFinish(false);
      setResults([]);
    }
  }, [submitted]);

  const onChangeHandler = (value: string) => {
    setLicensePlate(value);
    setFinish(false);
    setInvalidLicensePlate(false);
  };
  const onClearHandler = () => {
    setResults([]);
    setSubmitted(false);
    setLicensePlate('');
    setInvalidLicensePlate(false);
  };
  /* State dropdown change */
  // eslint-disable-next-line
  const changeState = (stateCode: any) => {
    setState(stateCode);
  };

  const renderResults = () => {
    return !_.isEmpty(results) ? (
      <>
        <FilterResult
          dataList={results}
          sort={true}
          value={licensePlate}
          onSelect={onVehicleSelect}
          replacements={replacements}
        />
      </>
    ) : (
      finish && _.trim(licensePlate).length >= 3 && (
        <div className='no-match'>
          <div className='no-found-text no-oil-text'>
            <p>
              {!invalidLicensePlate
                ? localesReplacement(replacements, 'No results found for')
                : localesReplacement(
                  replacements,
                  'Invalid License Plate number',
                )}
              <strong>{_.trim(licensePlate)}</strong>
            </p>
          </div>
        </div>
      )
    );
  };

  const inputClassName = ENABLED_LICENSE_PLATE_COUNTRIES.includes(country || '')
    ? `${cx('search-input')}`
    : `${cx('search-input')} ${cx('fluid')}`;

  return (
    <>
      <div className={cx('dropdown-list')}>
        {ENABLED_LICENSE_PLATE_COUNTRIES.includes(country || '') && (
          <div className={cx('dropdown-item')}>
            <FilterDropdown
              dataList={states}
              filter={state}
              changeHandler={changeState}
              type={
                country === 'GBR'
                  ? localesReplacement(replacements, 'Country')
                  : localesReplacement(replacements, 'State')
              }
              replacements={replacements}
            />
          </div>
        )}
        <div className={inputClassName}>
          {captcha && (
            <>
              <div
                id='captcha-container'
                className={cx('recaptcha-container')}
              ></div>
              <GoogleReCaptchaProvider
                reCaptchaKey={
                  process.env.REACT_APP_RECAPCHA_SITEKEY ||
                  '6Ld2oX4UAAAAACqWA9-3ostCypVeQRd4JPIT6dZR'
                }
                language={language?.slice(0, -1).toLowerCase()}
                useRecaptchaNet={false}
                useEnterprise={false}
                scriptProps={{
                  async: false, // optional, default to false,
                  defer: false, // optional, default to false
                  appendTo: 'head', // optional, default to "head", can be "head" or "body",
                  nonce: undefined, // optional, default undefined
                }}
                container={{
                  // optional to render inside custom element
                  element: 'captcha-container',
                  parameters: {
                    badge: 'inline', // optional, default undefined
                    theme: 'dark', // optional, default undefined
                  },
                }}
              >
                <GoogleReCaptcha
                  onVerify={onVerify}
                  refreshReCaptcha={refreshReCaptcha}
                />
              </GoogleReCaptchaProvider>
            </>
          )}
          <input
            type='text'
            value={licensePlate}
            onChange={(ev: { target: { value: string } }) =>
              onChangeHandler(ev.target.value)
            }
            placeholder={localesReplacement(
              replacements,
              'Enter license plate',
            )}
            maxLength={36}
          />
          <button className={style.submit} onClick={() => setSubmitted(true)}>
            {localesReplacement(replacements, 'Search')}
          </button>
          {!!licensePlate.length && (
            <i className={cx('remove')} onClick={onClearHandler}></i>
          )}
          {country === 'DEU' && (
            <i
              className={cx('hsn')}
              onMouseEnter={() => {
                if (!hsnTooltipOpen) {
                  sethsnTooltipOpen(true);
                }
              }}
              onMouseOut={() => {
                sethsnTooltipOpen(false);
              }}
            ></i>
          )}
          {hsnTooltipOpen && <div className={cx('hsn_tooltip')}></div>}
          <i className={cx('voice')}></i>
        </div>
      </div>

      {!isMount ? renderResults() : <Loading />}
    </>
  );
}

export default LicensePlateSearch;
